import { ArticleType, NewsFileT } from "@interfaces/legislation-news";
import { ErrorIndicator, Loader } from "@ui/indicators";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { setTitle } from "@store/title-store";

import { Modal } from "@ui/modal";
import NewsItems from "@components/news-components";
import { TextPageComponent } from "@feature/text-page-component/text-page-component";
import clsx from "clsx";
import style from "@scss/pages/news-and-practice/news-and-practice.module.scss";
import {
  useNewsItemQuery,
  useOtherNewsQuery,
  useDownloadNewsMutation,
} from "@lib/news";
import { getNewsArticleBreadcrumbs } from "@utils/news";
import { downloadBlob } from "@utils/downloadBlob";

function getPathToFile(type: ArticleType): string {
  switch (type) {
    case "LEGISLATION":
      return "uploads/legislation-news/";
    case "HELP":
      return "uploads/useful-materials/";
    case "PRACTICES":
      return "uploads/judicial-practices/";
    case "DEVELOPMENT":
      return "uploads/development-news/";
    case "PROJECT":
      return "uploads/project-news/";
    default:
      return "uploads/";
  }
}

function convertPath(type: ArticleType, files: NewsFileT[]): string[] {
  const basePath = getPathToFile(type);
  return files.map((file) => basePath + file.filename);
}

const ViewNews: React.FC<{
  path: ArticleType;
  setEdit?: (value: boolean) => void;
  setCurrentItem?: (value: { id: string; title: string }) => void;
}> = ({ path, setEdit, setCurrentItem }) => {
  const [openModalDownload, setOpenModalDownload] = useState(false);
  const { id } = useParams<{ id: string }>();

  const {
    data: currentItem,
    isLoading,
    isError,
  } = useNewsItemQuery(id, path);

  useEffect(() => {
    const breadcrumbs = getNewsArticleBreadcrumbs(path);
    if (currentItem) {
      breadcrumbs.push({
        title: currentItem.title,
        href: `${path}/${currentItem._id}`,
      });
    }

    setTitle(breadcrumbs);
    if (setEdit) setEdit(true);
    if (setCurrentItem && currentItem) {
      setCurrentItem({
        id: currentItem._id,
        title: currentItem.title,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem]);

  const { data: otherNews } = useOtherNewsQuery(id!, path);

  const downloadFileMutation = useDownloadNewsMutation(path);

  const handlerOpenDownFile = () => {
    setOpenModalDownload(true);
  };

  const handleDownloadFile = (file: NewsFileT) => {
    downloadFileMutation.mutate(file.filename, {
      onSuccess: (blob) => {
        downloadBlob(blob, file.originalname);
      },
    });
  };

  return (
    <>
      {isError ? (
        <ErrorIndicator />
      ) : isLoading ? (
        <Loader />
      ) : !currentItem ? null : (
        <TextPageComponent
          id={currentItem._id}
          files={currentItem.files}
          title={currentItem.title}
          images={convertPath(path, currentItem.images)}
          tags={currentItem.tags}
          createdAt={currentItem.createdAt}
          text={currentItem.text}
          handlerOpenDownFile={handlerOpenDownFile}
        />
      )}

      {otherNews && otherNews.length > 0 && (
        <div className={clsx(style.additional_news)}>
          <p className={clsx(style.add_section_title)}>Читайте еще</p>
          <NewsItems items={otherNews} path={path.toLowerCase()} />
        </div>
      )}
      <Modal
        open={openModalDownload}
        onClose={() => setOpenModalDownload(false)}
      >
        <>
          {currentItem?.files &&
            currentItem.files.map((file, index) => {
              return (
                <ul key={index}>
                  <li className={style.onLinkDownload}>
                    <button onClick={() => handleDownloadFile(file)}>
                      {file.originalname}
                    </button>
                  </li>
                </ul>
              );
            })}
        </>
      </Modal>
    </>
  );
};

export default ViewNews;
